/**
 * Centralized forecast & revenue calculations
 * Single source of truth for stage probability weighting
 */

import { STAGE_META } from '../constants/Stages.js';
import { formatCurrencyShort } from './Utils.js';
import { scheduleMatchesDateFilter } from './DealFilters.js';
import { getFrequencyLabel, getAmountSuffix } from './InvoiceFrequency.js';

export function getStageProbability(stageKey) {
    return STAGE_META[stageKey]?.probability ?? 0;
}

export function getEffectiveForecastProbability(deal) {
    if (deal.forecastProbability != null && deal.forecastProbability !== '') {
        const fp = Number(deal.forecastProbability);
        if (!Number.isNaN(fp)) return Math.min(100, Math.max(0, fp));
    }
    if (deal.probability != null && deal.probability !== '') {
        return Number(deal.probability) || 0;
    }
    return getStageProbability(deal.dealStage);
}

export function forecastFromPlanned(plannedAmount, stageKey, deal = null) {
    const prob = deal ? getEffectiveForecastProbability(deal) : getStageProbability(stageKey);
    return (plannedAmount || 0) * (prob / 100);
}

export function forecastFromContract(contractValue, stageKey, deal = null) {
    const prob = deal ? getEffectiveForecastProbability(deal) : getStageProbability(stageKey);
    return (contractValue || 0) * (prob / 100);
}

const INVOICE_FREQ_SUFFIX = {
    monthly: '/mo',
    quarterly: '/qtr',
    halfYearly: '/6mo',
    yearly: '/yr',
    oneTime: ' once'
};

export function getInvoiceFrequencyLabel(frequency) {
    return getFrequencyLabel(frequency);
}

export function getInvoiceAmountSuffix(frequency) {
    return getAmountSuffix(frequency) || INVOICE_FREQ_SUFFIX[frequency] || '';
}

export function formatInvoiceAmountLabel(amount, frequency) {
    return `${formatCurrencyShort(amount || 0)}${getInvoiceAmountSuffix(frequency)}`;
}

export function scheduleInPeriod(schedule, filters) {
    if (filters.dateRangeAll) return true;
    return scheduleMatchesDateFilter(schedule, filters);
}

export function aggregateRevenueMetrics(schedules, deals, filters = {}) {
    const dealMap = Object.fromEntries(deals.map(d => [d.id, d]));

    let planned = 0;
    let forecast = 0;
    let actual = 0;

    schedules.forEach(s => {
        if (!scheduleInPeriod(s, filters)) return;
        const deal = dealMap[s.dealId];
        const amt = s.plannedAmount || 0;
        planned += amt;
        forecast += forecastFromPlanned(amt, deal?.dealStage, deal);
        actual += s.paidAmount || 0;
    });

    const achievement = planned > 0 ? (actual / planned) * 100 : 0;

    return { planned, forecast, actual, achievement };
}

export function getDealForecastValue(deal) {
    const contractValue = deal.contractValue || deal.dealAmount || deal.amount || 0;
    return forecastFromContract(contractValue, deal.dealStage, deal);
}

export function aggregateQuarterMetrics(schedules, deals, year, quarterNum) {
    const dealMap = Object.fromEntries(deals.map(d => [d.id, d]));
    let planned = 0;
    let forecast = 0;
    let actual = 0;
    let committed = 0;
    let atRisk = 0;
    let lostRevenue = 0;

    schedules.forEach(s => {
        const d = new Date(s.invoiceDate);
        const sq = Math.floor(d.getMonth() / 3) + 1;
        if (d.getFullYear() !== year || sq !== quarterNum) return;

        const deal = dealMap[s.dealId];
        const amt = s.plannedAmount || 0;
        const status = s.status || 'planned';

        planned += amt;
        forecast += forecastFromPlanned(amt, deal?.dealStage, deal);
        actual += s.paidAmount || 0;
        if (status === 'invoiced' || status === 'issued') committed += amt;
        if (status === 'cancelled') lostRevenue += amt;
        else if (status === 'at_risk') atRisk += amt;
    });

    const achievement = planned > 0 ? Math.round((actual / planned) * 100) : 0;

    return { planned, forecast, actual, committed, atRisk, lostRevenue, achievement };
}
